import { type ComponentProps } from 'react';
import { type Dispatch } from 'redux';
import { type TFunction } from 'i18next';
import moment, { type Moment, type MomentInput } from 'moment';
import axios, { AxiosError, isAxiosError, isCancel } from 'axios';
import type { AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';
import { captureException } from '@sentry/react';
import jwtDecode from 'jwt-decode';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import history from '@history';
import { CircularProgress } from '@mui/material';
import type { GridRowId } from '@mui/x-data-grid-premium';

import { hideMessage, showMessage } from 'app/store/fuse/messageSlice';
import { type CancelablePromiseType } from './hooks/useCancelablePromise';

type FormattedNumberProps = Omit<ComponentProps<typeof FormattedNumber>, 'value'>;

interface Address {
	address?: string;
	city?: string;
	zip?: string;
	district?: string;
	country?: string;
}

export interface Duration {
	days?: number;
	hours?: number;
	minutes?: number;
	seconds?: number;
}

export interface ApiError {
	status?: number;
	data: { type: string; message: string };
}

interface ExecuteApiWrapperProps<ResponseType = any> {
	request: (config: AxiosRequestConfig<any>) => Promise<AxiosResponse>;
	config?: AxiosRequestConfig<any>;
	dispatch: Dispatch<any>;
	cancelToken?: CancelToken;
	getCancelablePromise?: CancelablePromiseType;
	successHandler?: (response: AxiosResponse<ResponseType>) => any;
	errorHandler?: (error: ApiError) => any;
}

interface ExecuteDownloadFileProps {
	url: string;
	body: any;
	fileName: string;
	dispatch: Dispatch<any>;
}

export type ExecuteApiProps = {
	url: string;
	body?: any;
	config?: AxiosRequestConfig;
} & Omit<ExecuteApiWrapperProps, 'request' | 'config'>;

type ExecuteStoredProps = {
	action: string;
	params?: any;
	data?: any;
} & Omit<ExecuteApiProps, 'url' | 'body' | 'config'>;

interface GetCoordinatesResponseType {
	lat?: string;
	lng?: string;
}

interface GetCoordinatesProps {
	address: Address;
	dispatch: Dispatch<any>;
	sysSettings: any;
}

export function trim(value) {
	return typeof value === 'string' ? value.trim() : value;
}

export function getBuildDate() {
	return import.meta.env.DEV ? moment.utc().format('yyyy-MM-DDTHH:mm:ss') : '__buildDate__';
}

export function roundDecimal(num: number, decimals: number) {
	return Math.round((num + Number.EPSILON) * 10 ** decimals) / 10 ** decimals;
}

export function snakeToCamelCase(str: string) {
	return str.toLowerCase().replace(/[-_][a-z0-9]/g, group => group.slice(-1).toUpperCase());
}

export function getItemId(item: any, idField: string = 'id', settings?: any): GridRowId {
	const itemId =
		item[idField] || item[`${settings?.modu_short}_uid`] || item[`${settings?.modu_short}_id`] || item.id;
	if (item !== undefined) return itemId;
	throw new Error(`[getItemId]: Missing ID for ${item}`);
}

export function today() {
	return moment().startOf('day').utc();
}

export function parseDate(date?: MomentInput) {
	return date && date !== null ? moment.utc(date).local() : undefined;
}

export function parseDateIsoWeek(date?: MomentInput) {
	return date && date !== null ? moment.utc(date).format('E-WW-GGGG') : undefined;
}

export function dateDiffWorkdays(startDate?: Moment, endDate?: Moment) {
	if (!startDate || !endDate || startDate?.isAfter(endDate)) return;

	let numWorkDays = 0;
	let currentDate = startDate.clone();

	while (currentDate <= endDate) {
		// Skips Sunday and Saturday
		if (currentDate.day() !== 0 && currentDate.day() !== 6) {
			numWorkDays++;
		}

		currentDate = currentDate.add(1, 'day');
	}

	return numWorkDays;
}

export function formatDuration({ days, hours, minutes, seconds }: Duration): string {
	const duration = moment.duration(
		(((days || 0) * 24 + (hours || 0)) * 60 + (minutes || 0)) * 60 + (seconds || 0),
		'seconds'
	) as any;
	const format = `${days ? 'd[gg] ' : ''}${days || hours ? 'h[h] ' : ''}m[m]${seconds ? ' s[s]' : ''}`;
	return duration.format(format, { trim: 'both' });
}

export function formatCurrency(value?: number, options?: FormattedNumberProps) {
	return (
		<FormattedNumber value={value || 0} style="currency" currency="EUR" useGrouping="always" {...(options || {})} />
	);
}

export function formatPercentage(value: number, options?: FormattedNumberProps) {
	return <FormattedNumber value={value / 100} style="percent" {...(options || {})} />;
}

export function formatSearchString(string?: string) {
	return string?.replace(/[^a-zA-Z0-9\s]/g, '');
	// return string.replace(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, '');
}

export function formatAddress2({ t, address }: { t: TFunction; address: Omit<Address, 'address'> }) {
	return <FormattedMessage id="address2" defaultMessage={t('ADDRESS2')} values={address as { [key: string]: any }} />;
}

export function calcObjectsTotal(array: any[], field: string) {
	return array.reduce((prev, current) => prev + (current[field] || 0), 0);
}

export function alertMsg(msg: string, variant: string, duration?: number) {
	return showMessage({
		message: msg.toString(), // text or html
		autoHideDuration: duration || 6000, // ms
		anchorOrigin: {
			vertical: 'top', // top bottom
			horizontal: 'center' // left center right
		},
		variant // success error info warning null
	});
}

export function closeAlertMsg() {
	return hideMessage();
}

export function alertProgressMsg(t: TFunction) {
	return showMessage({
		message: t('ALERT_UPDATING'),
		action: <CircularProgress size={24} color="inherit" disableShrink />,
		autoHideDuration: null,
		anchorOrigin: {
			vertical: 'top',
			horizontal: 'center'
		},
		variant: 'warning'
	});
}

export function alertLocalizedMsg(t: TFunction, message: string, variant: string) {
	return alertMsg(t(message), variant);
}

export function alertSuccessMsg(t: TFunction, message?: string) {
	return alertMsg(t(message || 'ALERT_MSG_SUCCESS'), 'success', 3000);
}

export function alertApiError({ status, data }: ApiError) {
	if ('vibrate' in window.navigator) window.navigator.vibrate(300);
	return alertMsg(`${data.type || status}${data.message ? `: ${data.message}` : ''}`, 'error');
}

export function openLink(params: { pathname: string; state?: any; newTab?: boolean; external?: boolean }) {
	if (params.newTab && !isPWA()) window.open(params.pathname, '_blank', 'noreferrer');
	else if (params.external) window.open(params.pathname, '_self');
	else history.push(params.pathname, params.state);
}

export function sendMail(mail: string) {
	window.open(`mailto:${mail}`);
}

export function call(number: string) {
	window.open(`tel:${number}`);
}

export function openMaps(address: string) {
	window.open(`https://www.google.com/maps/search/${encodeURIComponent(address)}`);
}

export function delayPromise(requestPromise: Promise<any>, timeout = 300) {
	const delayer = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
	return Promise.all([requestPromise, delayer(timeout)]).then(([response]) => response);
}

export function errorHandlerWrapper(params: {
	dispatch: Dispatch<any>;
	error: AxiosError<any>;
	errorHandler?: ExecuteApiWrapperProps['errorHandler'];
}) {
	const apiError: ApiError = {
		status: 500,
		data: { type: 'CONNECTION ERROR', message: 'Check your network connection and try again.' }
	};

	if (isAxiosError(params.error) && params.error.response) {
		const { status, config, data } = params.error.response;

		if (data?.type === 'middlewhere.Middlewares.AppVersionException') {
			openLink({ pathname: '/version-update', state: { error: data } });
		} else {
			// if (status === 400 || status >= 500) {
			apiError.status = status;
			if (data) {
				apiError.data = {
					type: data.title,
					message: `${status}${data?.detail && ` - ${data.detail}`}`
					// stack: data.stack
				};
			}

			captureException(params.error, scope => {
				if (config) scope.setContext('config', config as any);
				scope.setContext('response', data);
				return scope;
			});
			// }

			params.dispatch(closeAlertMsg());
			if (params.errorHandler) params.errorHandler(apiError);
			else params.dispatch(alertApiError(apiError));
		}
	}

	return apiError;
}

function executeApiWrapper(params: ExecuteApiWrapperProps) {
	const { request, config, dispatch, cancelToken, getCancelablePromise, successHandler, errorHandler } = params;
	const headers = { ...config?.headers, 'X-Client-Version': getBuildDate() };
	const apiRequest = request({ ...config, headers, cancelToken });

	return (getCancelablePromise?.(apiRequest) || apiRequest)
		.then(response => successHandler?.(response) || response)
		.catch(error => {
			if (isCancel(error)) return;
			errorHandlerWrapper({ dispatch, error, errorHandler });
			throw error;
		});
}

export const executeApi = {
	get: ({ url, body, ...params }: ExecuteApiProps) =>
		executeApiWrapper({ request: config => axios.get(url, config), ...params }),
	post: ({ url, body, ...params }: ExecuteApiProps) => {
		const request = config =>
			axios.post(url, body, { ...config, headers: { ['Content-Type']: 'application/json', ...config.headers } });
		return executeApiWrapper({ request, ...params });
	},
	put: ({ url, body, ...params }: ExecuteApiProps) => {
		const request = config =>
			axios.put(url, body, { ...config, headers: { ['Content-Type']: 'application/json', ...config.headers } });
		return executeApiWrapper({ request, ...params });
	},
	delete: ({ url, body, ...params }: ExecuteApiProps) =>
		executeApiWrapper({ request: config => axios.delete(url, config), ...params })
};

export function executeStored({ action, params, data, ...otherParams }: ExecuteStoredProps) {
	const body = JSON.stringify({ action, params, data });
	return executeApi.post({ url: '/api/execute', body, ...otherParams });
}

export function executeDownloadFile({ url, body, fileName, dispatch }: ExecuteDownloadFileProps) {
	const link = document.createElement('a');
	link.target = '_blank';
	link.download = fileName;

	const successHandler = response => {
		link.href = URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
		link.click();
	};

	return executeApi.post({ url, config: { responseType: 'blob' }, body, dispatch, successHandler });
}

export function getCoordinatesConnector({ sysSettings }: { sysSettings: any }) {
	if (sysSettings.geoapify_integration === 'true') return 'geoapify';
	if (sysSettings.hereapi_integration === 'true') return 'here';

	return undefined;
}

export function getCoordinates({ address, dispatch, sysSettings }: GetCoordinatesProps) {
	const addressString = `${address.address || ''} ${address.city || ''} ${address.zip || ''} ${address.district || ''} ${address.country || ''}`;
	const encodedAddress = encodeURIComponent(addressString.replace(/^[a-zA-Z0-9_]*$/, ''));

	const connector = getCoordinatesConnector({ sysSettings });

	if (connector) {
		const url = `/api/perceptive/${connector}/coordinates?address=${encodedAddress}`;
		return executeApiWrapper({ request: config => axios.get<GetCoordinatesResponseType>(url, config), dispatch });
	}

	return undefined;
}

// TODO
export async function sendMailNotification({ data }: { data: any }) {
	return axios
		.post('/api/notification/mail/send', data)
		.then(response => {})
		.catch(error => {});
}

export function generatePastelColor() {
	let red = getRandomFromRange(0, 256);
	let green = getRandomFromRange(0, 256);
	let blue = getRandomFromRange(0, 256);

	const mixRed = 1 + 0xff / 256;
	const mixGreen = 1 + 0xff / 256;
	const mixBlue = 1 + 0xff / 256;

	// red = Math.floor((red + mixRed) / 2);
	// green = Math.floor((green + mixGreen) / 2);
	// blue = Math.floor((blue + mixBlue) / 2);
	red = Math.floor(red + mixRed);
	green = Math.floor(green + mixGreen);
	blue = Math.floor(blue + mixBlue);
	return `#${intToHexColorPart(red)}${intToHexColorPart(green)}${intToHexColorPart(blue)}`;
}

function intToHexColorPart(c: number) {
	const hex = c.toString(16);
	return hex.length === 1 ? `0${hex}` : hex;
}

function getRandomFromRange(min: number, max: number) {
	return Math.random() * (max - min) + min;
}

export function isPWA() {
	return navigator.standalone || window.matchMedia('(display-mode: minimal-ui)').matches; // NOTE: display-mode must be the same as display in manifest.json
}

// TODO this function is kept to prevent crashes with navigation.permissions.query of type microphone in the useSpeechToText() hook when on Firefox
export function isFirefox() {
	return (window.navigator.userAgent.match(/Firefox.+?/)?.length || 0) > 0;
}

export function getConnectionFromToken() {
	const accessToken = window.localStorage.getItem('accessToken');

	if (accessToken) {
		const claims_userData = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata';
		const connJSON = JSON.parse(jwtDecode<any>(accessToken)[claims_userData]);
		return connJSON.Connection;
	}

	return null;
}

export function blobToBase64(blob: Blob) {
	return new Promise(resolve => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result);
		reader.readAsDataURL(blob);
	}).then(result => {
		if (result === null) return undefined;
		else if (typeof result === 'string') {
			return result.substring(result.indexOf(',') + 1).trim();
		} else {
			const buf = new ArrayBuffer(64);
			const decoder = new TextDecoder();
			return decoder.decode(buf);
		}
	});
}

export default {
	alertApiError,
	alertMsg,
	alertLocalizedMsg,
	alertProgressMsg,
	alertSuccessMsg,
	blobToBase64,
	calcObjectsTotal,
	call,
	closeAlertMsg,
	dateDiffWorkdays,
	delayPromise,
	errorHandlerWrapper,
	executeApi,
	executeDownloadFile,
	executeStored,
	formatAddress2,
	formatCurrency,
	formatDuration,
	formatPercentage,
	formatSearchString,
	generatePastelColor,
	getBuildDate,
	getConnectionFromToken,
	getCoordinatesConnector,
	getCoordinates,
	getItemId,
	isFirefox,
	isPWA,
	openLink,
	openMaps,
	parseDate,
	parseDateIsoWeek,
	roundDecimal,
	sendMail,
	sendMailNotification,
	snakeToCamelCase,
	today,
	trim
};
